import React, { useRef, useState } from "react";
import PropTypes, { bool } from "prop-types";
import styles from "./File.module.css";
import Button from "./Button";
import { FileDrop } from "react-file-drop";
import useFetch from "@maxis/client-logic/hooks/useFetch";
import { findRoutePath } from "@maxis/client-logic/utils/menuUtil";
import useMessageBox from "@maxis/client-logic/hooks/useMessageBox";

/**
 * FileMulti
 * @type React.FC<FileMultiPropTypes>
 */
const FileMulti = React.forwardRef((props, ref) => {
  const { alert } = useMessageBox();

  const isHidden = props.isHidden ?? false;

  Object.keys(props).forEach((p) => {
    if (!Object.keys(FileMultiPropTypes).includes(p)) {
      console.error(`FileMulti: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /**
   * FileMulti Component의 State
   */
  const [currState, setCurrState] = useState({
    readOnly: props.readOnly,
    attachSeq: null,
    filesInDB: [],
    filesToUpload: [],
    saveInDB: props.saveInDB,
    publicPath: props.publicPath,
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 파일 목록 조회 */
    loadFiles(attachSeq, imageView = false) {
      return _loadFileListByAttachSeq(attachSeq, imageView);
    },
    loadFileList(attachSeq, imageView = false) {
      _loadFileListByAttachSeq2(attachSeq, imageView);
    },
    setReadOnly(b) {
      setCurrState((prev) => ({ ...prev, readOnly: b }));
    },
    /** 파일 저장 */
    async saveFiles(publicPath = false, requiredFile = true) {
      /* ATTACH_SEQ가 존재하지않고, 업로드할 파일이 없을 때 => 신규 + 파일업로드 X => 서비스 호출 X */
      if (!currState.attachSeq && currState.filesToUpload.length <= 0)
        return null;

      const filesToDelete = currState.attachSeq
        ? currState.filesInDB
            .filter((f) => f.delete)
            .map((f) => f.ATTACH_FILE_SEQ)
        : [];

      /* ATTACH_SEQ가 존재하고, 삭제된 파일, 추가된 파일이 없을 때 => 수정사항 없음 => 서비스 호출 X */
      if (
        currState.attachSeq &&
        filesToDelete.length <= 0 &&
        currState.filesToUpload.length <= 0
      ) {
        return currState.attachSeq;
      }

      const lastOrd =
        currState.filesInDB.length > 0
          ? currState.filesInDB[currState.filesInDB.length - 1].ORD
          : 0;

      const param = {
        ATTACH_SEQ: currState.attachSeq || "",
        FILES_TO_DELETE: JSON.stringify(filesToDelete),
        LAST_ORD: lastOrd,
        SAVE_IN_DB: currState.saveInDB,
        PUBLIC_PATH: publicPath,
        REQUIRED_FILE: requiredFile,
      };
      const response = await request(
        "/file-upload",
        {
          body: param,
          attachFiles: currState.filesToUpload,
        },
        { showLoad: false },
      );

      if (response) {
        return response.ATTACH_SEQ;
      }
    },

    getAttachSeq() {
      return currState.attachSeq;
    },

    getFilesToUpload() {
      return currState.filesToUpload;
    },

    /** 초기화 */
    clear() {
      setCurrState({
        readOnly: props.readOnly,
        attachSeq: null,
        filesInDB: [],
        filesToUpload: [],
      });
    },

    enable() {},
    getFileType() {
      const filesInDBList = currState.filesInDB
        .filter((x) => {
          return !x.delete;
        })
        .map((x) => {
          return x.FILE_TYPE;
        });
      const filesToUploadList = currState.filesToUpload.map((x) => {
        return x.type;
      });

      const type = [...filesToUploadList, ...filesInDBList];

      return type;
    },
  }));

  const { request, downloadFile, downloadFileBase64 } = useFetch();
  const refFileInput = useRef();

  /**
   * 파일 추가
   * @param {*} files input으로부터 선택된 file 목록
   */
  const _onFileAdd = (files) => {
    if (
      props.uploadSingle &&
      (files.length > 1 || currState.filesToUpload.length > 0)
    ) {
      if (refFileInput.current.value !== null) refFileInput.current.value = "";
      alert(
        "안내",
        "업로드 가능한 파일갯수를 초과하였습니다. 파일갯수를 확인하세요.",
      );
      return;
    }

    //accept 정보에 따른 추가 여부 판단.
    if (props.acceptValue !== null && props.acceptValue !== "*.*") {
      //alert('', `필터 정보는 [${props.acceptValue}] 입니다.`);

      for (let file of files) {
        console.log(file.type);
        console.log(file);
      }

      let chk = true;
      switch (props.acceptValue) {
        case "image/*":
          for (let file of files) {
            if (file.type.indexOf("image") < 0) {
              chk = false;
              break;
            }
          }
          if (!chk) {
            alert("", "이미지 파일만 추가해 주시기 바랍니다.");
            return;
          }
          break;
        default:
          alert(
            "",
            `해당 필터 정보는 [${props.acceptValue}] 예외처리 되지 않았습니다.`,
          );
          break;
      }
    }

    if (props.dsOption) {
      if (_dsValidationCheck(files)) {
        if (refFileInput.current.value !== null)
          refFileInput.current.value = "";
        return;
      }
    }

    setCurrState((prev) => ({
      ...prev,
      filesToUpload: [...prev.filesToUpload, ...files],
    }));

    if (refFileInput.current.value !== null) refFileInput.current.value = "";
  };

  /**
   * props.dsOption : true ==> Digital Signage에서 파일 조작할때 유효성 체크
   * @param {*} files
   */
  const _dsValidationCheck = (files) => {
    const imgCnt = currState.filesToUpload.filter(
      (x) => x.type.indexOf("image") > -1,
    ).length;
    const videoCnt = currState.filesToUpload.filter(
      (x) => x.type.indexOf("video") > -1,
    ).length;

    for (let file of files) {
      if (file.type.indexOf("image") > -1) {
        if (videoCnt > 0 && imgCnt > 0) {
          alert("", `썸네일은 한장만 업로드 가능합니다.`);
          return true;
        }
      } else if (file.type.indexOf("video") > -1) {
        if (videoCnt > 0) {
          alert("", `영상은 한번에 하나만 업로드 가능합니다.​`);
          return true;
        }
        if (imgCnt > 1) {
          alert(
            "",
            `썸네일은 한장만 업로드 가능합니다. \r등록된 이미지를 삭제 후 영상을 업로드 해주세요.​`,
          );
          return true;
        }
      } else {
        alert("", `이미지나 영상 파일만 업로드가능합니다.`);
        return true;
      }
    }

    return false;
  };

  /**
   * 업로드 대상 파일 목록에서 제거
   * @param {*} fileToRemove
   */
  const _removeFromFilesToUpload = (fileToRemove) => {
    const remainedFiles = currState.filesToUpload.filter(
      (f) => f !== fileToRemove,
    );
    setCurrState((prev) => ({
      ...prev,
      filesToUpload: remainedFiles,
    }));

    if (refFileInput.current.value !== null) refFileInput.current.value = "";
  };

  /**
   * 이미 저장되어 있는 파일 목록에서 삭제대상으로 Flag 처리
   * @param {*} fileToRemove
   * @param {*} cancel
   */
  const _removeFromFilesInDB = (fileToRemove, cancel = false) => {
    const files = currState.filesInDB.map((f) => ({
      ...f,
      delete: f !== fileToRemove ? f.delete : f === fileToRemove && !cancel,
    }));

    setCurrState((prev) => ({
      ...prev,
      filesInDB: files,
    }));
  };

  /**
   * 파일 목록 조회
   * @param {*} attachSeq
   */
  const _loadFileListByAttachSeq = async (attachSeq, imageView) => {
    const param = {
      ATTACH_SEQ: attachSeq,
    };
    const response = await request(
      "/file-list",
      {
        body: param,
      },
      { showLoad: false },
    );

    const files = response ?? [];

    setCurrState((prev) => ({
      ...prev,
      attachSeq: attachSeq,
      filesInDB: files,
      filesToUpload: [],
    }));

    if (imageView && files.length > 0) {
      _preViewFile(
        files[0].ATTACH_FILE_SEQ,
        files[0].ORG_FILE_NM,
        currState.saveInDB,
      );
    }

    return files;
  };

  const _preViewFile = async (seq, name, db) => {
    const fileStream = await downloadFileBase64(seq, db);
    props.callBackAction(seq, name, fileStream);
  };

  /**
   * 파일 목록 조회2
   * @param {*} attachSeq
   */
  const _loadFileListByAttachSeq2 = async (attachSeq, imageView) => {
    const param = {
      ATTACH_SEQ: attachSeq,
    };
    const response = await request(
      "/file-list",
      {
        body: param,
      },
      { showLoad: false },
    );

    const files = response ?? [];

    setCurrState((prev) => ({
      ...prev,
      attachSeq: attachSeq,
      filesInDB: files,
      filesToUpload: [],
    }));

    let fileDataList = [];
    if (imageView && files.length > 0) {
      files.forEach((item) => {
        fileDataList.push({
          ATTACH_FILE_SEQ: item.ATTACH_FILE_SEQ,
          ORG_FILE_NM: item.ORG_FILE_NM,
          DB: currState.saveInDB,
        });
      });
      _preViewFile2(fileDataList);
    }
  };

  const _preViewFile2 = async (fileDataList) => {
    let fileList = [];
    fileDataList.forEach((item) => {
      let fileStream = downloadFileBase64(item.ATTACH_FILE_SEQ, item.DB);
      fileList.push({
        seq: item.ATTACH_FILE_SEQ,
        name: item.ORG_FILE_NM,
        fileStream: fileStream,
      });
    });
    props.callBackAction(fileList);
  };

  return (
    <div className={styles.e3File} style={isHidden ? { display: "none" } : {}}>
      {!currState.readOnly ? (
        <>
          <input
            style={{ display: "none" }}
            id="contentList"
            ref={refFileInput}
            type="file"
            accept={props.acceptValue}
            multiple
            onChange={(e) => _onFileAdd(e.target.files)}
          />
          <div className={styles.fileSelect}>
            <FileDrop
              className={styles.fileDropArea}
              onDrop={(files) => {
                _onFileAdd(files);
              }}
              onTargetClick={() => {
                refFileInput.current?.click();
              }}
            >
              <Button
                label={""}
                colorType="colorNoBorder"
                iconType={"btn_add"}
                authBtn={"SEARCH"}
                checkAuth={true}
                onClick={() => {}}
              />
              <p style={{ marginTop: "5px", color: "#666", fontWeight: "500" }}>
                혹은 여기로 파일을 끌어와 주세요.
              </p>
            </FileDrop>
          </div>
        </>
      ) : null}

      <div className={styles.listSection}>
        <ul
          style={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: props.fileListDirection,
          }}
        >
          {currState.filesInDB.map((file, i) => (
            <li key={i} className={styles.fileRow}>
              <span
                key={"fileName"}
                className={`${styles.fileName} ${file.delete ? styles.lineThrough : ""}`}
                onClick={() => {
                  props.preView
                    ? _preViewFile(
                        file.ATTACH_FILE_SEQ,
                        file.ORG_FILE_NM,
                        currState.saveInDB,
                      )
                    : null;
                }}
              >
                {file.ORG_FILE_NM}
              </span>
              {file.delete ? null : (
                <Button
                  key={"download"}
                  label="다운로드"
                  colorType="default-outline"
                  iconType={"btn_down"}
                  size={"small"}
                  authBtn={"DOWNLOAD"}
                  checkAuth={true}
                  onClick={() => {
                    downloadFile(file.ATTACH_FILE_SEQ, currState.saveInDB);
                  }}
                />
              )}

              {!currState.readOnly ? (
                file.delete ? (
                  <Button
                    key={"cancel"}
                    label="삭제취소"
                    colorType="default-outline"
                    iconType={"btn_del"}
                    size={"small"}
                    authBtn={"UPLOAD"}
                    checkAuth={true}
                    onClick={() => {
                      _removeFromFilesInDB(file, true);
                    }}
                  />
                ) : (
                  <Button
                    key={"delete"}
                    label="삭제"
                    colorType="default-outline"
                    iconType={"btn_del"}
                    size={"small"}
                    authBtn={"DELETE"}
                    checkAuth={true}
                    onClick={() => {
                      _removeFromFilesInDB(file);
                    }}
                  />
                )
              ) : null}
            </li>
          ))}
          {currState.filesToUpload.map((file, i) => (
            <li key={i + "file"} className={styles.fileRow}>
              <span key={i + "fileName"} className={styles.fileName}>
                {file.name}
              </span>
              <Button
                key={i + "delete"}
                label="삭제"
                iconType={"del"}
                size={"small"}
                authBtn={"DELETE"}
                checkAuth={true}
                onClick={() => {
                  _removeFromFilesToUpload(file);
                }}
              />
            </li>
          ))}
        </ul>
      </div>
      {props.remark ? (
        <div className={styles.description}> {props.remark} </div>
      ) : null}
    </div>
  );
});

const FileMultiPropTypes = {
  readOnly: PropTypes.bool,
  remark: PropTypes.node,
  saveInDB: PropTypes.bool,
  fileListDirection: PropTypes.string, // row, column
  dsOption: PropTypes.bool,
  uploadSingle: PropTypes.bool,
  publicPath: PropTypes.bool,
  callBackAction: PropTypes.func,
  acceptValue: PropTypes.string,
  preView: PropTypes.bool,
  isHidden: PropTypes.bool,
};

FileMulti.propTypes = FileMultiPropTypes;
FileMulti.defaultProps = {
  readOnly: false,
  saveInDB: false,
  remark: <p>첨부파일은 30MB까지 가능합니다.</p>,
  fileListDirection: "row",
  dsOption: false,
  uploadSingle: false,
  publicPath: false,
  callBackAction: null,
  acceptValue: "*.*",
  preView: false,
  isHidden: false,
};
FileMulti.displayName = "FileMulti";
export default FileMulti;
